import React from "react";

import { InventoryChangeReason } from "__graphql__/types";
import { IntlMessageId } from "shared/lang";
import {
  BoxesDeliveryGroupIcon,
  CalendarWarningIcon,
  EssentialToolsIcon,
  PackageBoxWarningNewIcon,
  PackageDeliveryReturnIcon,
  PoliceCarIcon,
  RollerWhiskIcon,
  ShoppingBagIcon,
  InboundIcon,
  InventoryIcon,
  StockCheckIcon,
} from "ui/Icons/Icons";

export const reasonMetadata: Record<
  string,
  {
    icon: React.ReactElement<any>;
    label: IntlMessageId;
    countLabelId?: IntlMessageId;
    explanationTitleId?: IntlMessageId;
    explanationDescriptionId?: IntlMessageId;
  }
> = {
  [InventoryChangeReason.inbound_goods_received]: {
    icon: <InboundIcon />,
    label: "inventory-change-reason.inbound_goods_received",
  },
  [InventoryChangeReason.rolling_inventory_check]: {
    icon: <InventoryIcon />,
    label: "inventory-change-reason.rolling_inventory_check",
  },
  [InventoryChangeReason.low_stock_check]: {
    icon: <StockCheckIcon />,
    label: "inventory-change-reason.low_stock_check",
  },
  [InventoryChangeReason.out_of_stock_check]: {
    icon: <StockCheckIcon />,
    label: "inventory-change-reason.out_of_stock_check",
  },
  [InventoryChangeReason.extra_check]: {
    icon: <StockCheckIcon />,
    label: "inventory-change-reason.expensive_check",
  },
  [InventoryChangeReason.outbound_stock_transfer]: {
    icon: <BoxesDeliveryGroupIcon />,
    label: "inventory-change-reason.outbound_stock_transfer",
  },
  [InventoryChangeReason.inbound_stock_transfer]: {
    icon: <InboundIcon />,
    label: "inventory-change-reason.inbound_stock_transfer",
  },
  [InventoryChangeReason.outbound_hub_closure]: {
    icon: <BoxesDeliveryGroupIcon />,
    label: "inventory-change-reason.outbound_hub_closure",
  },
  [InventoryChangeReason.eoy_inventory_check]: {
    icon: <CalendarWarningIcon />,
    label: "inventory-change-reason.eoy_inventory_check",
  },
  [InventoryChangeReason.eoy_inventory_recount]: {
    icon: <CalendarWarningIcon />,
    label: "inventory-change-reason.eoy_inventory_recount",
  },
  [InventoryChangeReason.inbound_surprise_bag]: {
    icon: <ShoppingBagIcon />,
    label: "inventory-change-reason.inbound_surprise_bag",
  },
  [InventoryChangeReason.outbound_surprise_bag]: {
    icon: <ShoppingBagIcon />,
    label: "inventory-change-reason.outbound_surprise_bag",
  },
  [InventoryChangeReason.post_inbound_correction]: {
    icon: <BoxesDeliveryGroupIcon />,
    label: "inventory-change-reason.post_inbound_correction",
  },
  [InventoryChangeReason.requested_from_inbound_correction]: {
    icon: <BoxesDeliveryGroupIcon />,
    label: "inventory-change-reason.requested_from_inbound_correction",
  },
  [InventoryChangeReason.inbound_order_cancellation]: {
    icon: <PackageBoxWarningNewIcon />,
    label: "inventory-change-reason.inbound_order_cancellation",
  },
  [InventoryChangeReason.outbound_order_cancellation_not_returned]: {
    icon: <PackageDeliveryReturnIcon />,
    label: "inventory-change-reason.outbound_order_cancellation_not_returned",
  },
  [InventoryChangeReason.outbound_order_cancellation_not_sellable]: {
    icon: <PackageDeliveryReturnIcon />,
    label: "inventory-change-reason.outbound_order_cancellation_not_sellable",
  },
  [InventoryChangeReason.outbound_product_damaged]: {
    icon: <PackageBoxWarningNewIcon />,
    label: "inventory-change-reason.outbound_product_damaged",
    countLabelId: "components.increasedecrease.how_many_are_damaged",
    explanationTitleId: "inventory-change-reason.explanations.outbound_product_damaged.title",
    explanationDescriptionId:
      "inventory-change-reason.explanations.outbound_product_damaged.description",
  },
  [InventoryChangeReason.outbound_delivery_product_damaged]: {
    icon: <PackageBoxWarningNewIcon />,
    label: "inventory-change-reason.outbound_delivery_product_damaged",
    countLabelId: "components.increasedecrease.how_many_are_damaged",
  },
  [InventoryChangeReason.outbound_product_expired]: {
    icon: <CalendarWarningIcon />,
    label: "inventory-change-reason.outbound_product_expired",
    countLabelId: "components.increasedecrease.how_many_are_expired",
    explanationTitleId: "inventory-change-reason.explanations.outbound_product_expired.title",
    explanationDescriptionId:
      "inventory-change-reason.explanations.outbound_product_expired.description",
  },
  [InventoryChangeReason.outbound_delivery_product_expired]: {
    icon: <CalendarWarningIcon />,
    label: "inventory-change-reason.outbound_delivery_product_expired",
    countLabelId: "components.increasedecrease.how_many_are_expired",
  },
  [InventoryChangeReason.outbound_too_good_to_go]: {
    icon: <ShoppingBagIcon />,
    label: "inventory-change-reason.outbound_too_good_to_go",
    countLabelId: "components.increasedecrease.how_many_are_rescue_bag",
    explanationTitleId: "inventory-change-reason.explanations.outbound_too_good_to_go.title",
    explanationDescriptionId:
      "inventory-change-reason.explanations.outbound_too_good_to_go.description",
  },
  [InventoryChangeReason.correction]: {
    icon: <BoxesDeliveryGroupIcon />,
    label: "inventory-change-reason.correction",
    countLabelId: "components.increasedecrease.how_many_are_stock_correction",
    explanationTitleId: "inventory-change-reason.explanations.correction.title",
    explanationDescriptionId: "inventory-change-reason.explanations.correction.description",
  },
  [InventoryChangeReason.outbound_equipment_failure]: {
    icon: <EssentialToolsIcon />,
    label: "inventory-change-reason.outbound_equipment_failure",
    countLabelId: "components.increasedecrease.how_many_are_equipment_failure",
    explanationTitleId: "inventory-change-reason.explanations.outbound_equipment_failure.title",
    explanationDescriptionId:
      "inventory-change-reason.explanations.outbound_equipment_failure.description",
  },
  [InventoryChangeReason.product_recall]: {
    icon: <PackageDeliveryReturnIcon />,
    label: "inventory-change-reason.product_recall",
    countLabelId: "components.increasedecrease.how_many_are_product_recall",
    explanationTitleId: "inventory-change-reason.explanations.product_recall.title",
    explanationDescriptionId: "inventory-change-reason.explanations.product_recall.description",
  },
  [InventoryChangeReason.correction_baking_ingredients]: {
    icon: <RollerWhiskIcon />,
    label: "inventory-change-reason.correction_baking_ingredients",
    countLabelId: "components.increasedecrease.how_many_are_correction_baking_ingredients",
    explanationTitleId: "inventory-change-reason.explanations.correction_baking_ingredients.title",
    explanationDescriptionId:
      "inventory-change-reason.explanations.correction_baking_ingredients.description",
  },
  [InventoryChangeReason.correction_break_in]: {
    icon: <PoliceCarIcon />,
    label: "inventory-change-reason.correction_break_in",
    countLabelId: "components.increasedecrease.how_many_are_breakin",
    explanationTitleId: "inventory-change-reason.explanations.correction_break_in.title",
    explanationDescriptionId:
      "inventory-change-reason.explanations.correction_break_in.description",
  },
};
